// AdminUsers — filterable + paginated user table with row actions.
// Phase 24 upgrade: country flags, plan, deals breakdown, AI spend, referrals
// columns + filters + a slide-in user-detail drawer with 6 tabs.

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Loader2, Sparkles, Globe, Database } from "lucide-react";
import UserDetailDrawer, { countryCodeToEmoji } from "@/components/admin/UserDetailDrawer";

const COUNTRY_FILTER_OPTIONS = [
  { code: "", label: "All countries" },
  { code: "US", label: "🇺🇸 United States" },
  { code: "IN", label: "🇮🇳 India" },
  { code: "GB", label: "🇬🇧 United Kingdom" },
  { code: "CA", label: "🇨🇦 Canada" },
  { code: "AU", label: "🇦🇺 Australia" },
  { code: "DE", label: "🇩🇪 Germany" },
  { code: "FR", label: "🇫🇷 France" },
  { code: "BR", label: "🇧🇷 Brazil" },
  { code: "MX", label: "🇲🇽 Mexico" },
  { code: "JP", label: "🇯🇵 Japan" },
];

export default function AdminUsers() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [suspended, setSuspended] = useState("all");
  // Phase 24 — new filters
  const [country, setCountry] = useState("");
  const [proOnly, setProOnly] = useState(false);
  const [hasReferrals, setHasReferrals] = useState(false);
  const [sortBy, setSortBy] = useState("created_desc");

  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [drawerUser, setDrawerUser] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    const params = { limit: 25, skip, intel: true };
    if (q) params.q = q;
    if (plan !== "all") params.plan = plan;
    if (suspended !== "all") params.suspended = suspended === "yes";
    if (country) params.country = country;
    if (proOnly) params.pro_only = true;
    if (hasReferrals) params.has_referrals = true;
    if (sortBy && sortBy !== "created_desc") params.sort = sortBy;
    const { data } = await api.get("/admin/users", { params });
    setRows(data.items);
    setTotal(data.total);
  }, [skip, plan, suspended, q, country, proOnly, hasReferrals, sortBy]);

  useEffect(() => { load(); }, [load]);

  const onSearch = async (e) => { e.preventDefault(); setSkip(0); await load(); };

  const toggleSuspend = async (u) => {
    const next = !u.is_suspended;
    await api.patch(`/admin/users/${u.id}`, { is_suspended: next });
    toast.success(next ? "User suspended" : "User unsuspended");
    load();
  };
  const resetPassword = async (u) => {
    const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
    toast.success(`Temp password: ${data.temporary_password}`, { duration: 15000 });
  };
  const impersonate = async (u) => {
    await api.post(`/admin/users/${u.id}/impersonate`);
    toast.success(`Now impersonating ${u.email}`);
    navigate("/dashboard");
  };
  const remove = async (id) => {
    await api.delete(`/admin/users/${id}`);
    setConfirmDelete(null);
    setDrawerUser(null);
    toast.success("User deleted");
    load();
  };
  const backfillGeo = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/users/backfill-geo?limit=200");
      toast.success(`Backfill: ${data.enriched} enriched of ${data.processed} processed.`, { duration: 6000 });
      load();
    } catch (e) {
      toast.error("Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (!rows) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading users…</div>;

  // ─── Top stats row ─────────────────────────────────────────────────────
  const proCount = rows.filter(r => ["pro", "premium", "enterprise"].includes((r.subscription_plan || "").toLowerCase())).length;
  const countries = rows.reduce((m, r) => {
    const c = r.signup_country_code || r.last_login_country_code;
    if (c) m[c] = (m[c] || 0) + 1;
    return m;
  }, {});
  const topCountry = Object.entries(countries).sort((a, b) => b[1] - a[1])[0];
  const newThisWeek = rows.filter((r) => {
    if (!r.created_at) return false;
    const ageDays = (Date.now() - new Date(r.created_at).getTime()) / 86400000;
    return ageDays <= 7;
  }).length;

  return (
    <div className="space-y-5 fade-in" data-testid="admin-users">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Users</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">User intelligence</h1>
        <p className="mt-1 text-sm text-slate-500">{total} total · paginated 25 at a time · click any row for full profile.</p>
      </div>

      {/* Top stat strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="stat-total">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Total users</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{total.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="stat-pro">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700">Pro on this page</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-900">{proCount}</div>
        </div>
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3" data-testid="stat-new7d">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-700">New ≤7d (page)</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-indigo-900">{newThisWeek}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="stat-topcountry">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Top country (page)</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {topCountry ? `${countryCodeToEmoji(topCountry[0])} ${topCountry[0]} · ${topCountry[1]}` : "—"}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <form onSubmit={onSearch} className="flex flex-wrap items-end gap-2">
        <div className="min-w-[14rem] flex-1">
          <Input
            data-testid="users-search"
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-white"
          />
        </div>
        <Select value={plan} onValueChange={(v) => { setSkip(0); setPlan(v); }}>
          <SelectTrigger className="w-32 bg-white" data-testid="users-plan-filter"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Select value={country || "all"} onValueChange={(v) => { setSkip(0); setCountry(v === "all" ? "" : v); }}>
          <SelectTrigger className="w-44 bg-white" data-testid="users-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent>
            {COUNTRY_FILTER_OPTIONS.map(c => (
              <SelectItem key={c.code || "all"} value={c.code || "all"}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={suspended} onValueChange={(v) => { setSkip(0); setSuspended(v); }}>
          <SelectTrigger className="w-36 bg-white" data-testid="users-suspended-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="no">Active</SelectItem>
            <SelectItem value="yes">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => { setSkip(0); setSortBy(v); }}>
          <SelectTrigger className="w-40 bg-white" data-testid="users-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">Newest first</SelectItem>
            <SelectItem value="login_count_desc">Most logins</SelectItem>
            <SelectItem value="ai_cost_desc">Highest AI spend</SelectItem>
            <SelectItem value="deals_count_desc">Most deals</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs">
          <input type="checkbox" checked={proOnly} onChange={(e) => { setSkip(0); setProOnly(e.target.checked); }} data-testid="filter-pro-only" />
          Pro only
        </label>
        <label className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs">
          <input type="checkbox" checked={hasReferrals} onChange={(e) => { setSkip(0); setHasReferrals(e.target.checked); }} data-testid="filter-has-referrals" />
          Has referrals
        </label>
        <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700"><Search className="me-1 h-4 w-4" /> Search</Button>
        <Button type="button" variant="outline" onClick={backfillGeo} disabled={backfilling} data-testid="backfill-geo-btn">
          <Globe className="me-1 h-4 w-4" /> {backfilling ? "Backfilling…" : "Backfill geo"}
        </Button>
      </form>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead className="text-center">Country</TableHead>
              <TableHead className="text-right">Logins</TableHead>
              <TableHead className="text-center">Deals</TableHead>
              <TableHead className="text-right">AI 30d</TableHead>
              <TableHead className="text-right">Refs</TableHead>
              <TableHead className="text-right">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => {
              const isPro = ["pro", "premium", "enterprise"].includes((u.subscription_plan || "").toLowerCase());
              const code = u.signup_country_code || u.last_login_country_code;
              return (
                <TableRow
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="cursor-pointer transition-colors hover:bg-slate-50"
                  onClick={() => setDrawerUser(u)}
                >
                  <TableCell>
                    <div className="font-medium text-slate-900">{u.name || "—"}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                    {u.is_admin && <span className="mt-1 inline-block rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-indigo-700">admin</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${isPro ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                      {isPro && <Sparkles className="h-2.5 w-2.5" />} {u.subscription_plan || "free"}
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-base">
                    {code ? (
                      <span title={u.signup_country || u.last_login_country || code}>
                        {countryCodeToEmoji(code)}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-slate-700">{u.login_count || 0}</TableCell>
                  <TableCell className="text-center font-mono text-xs text-slate-600">{u.deals_by_type_compact || `R${u.deal_count || 0}`}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-slate-700">
                    {u.ai_cost_30d_usd ? `$${u.ai_cost_30d_usd.toFixed(2)}` : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {u.referrals_made > 0 ? (
                      <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-bold text-emerald-700">🎁 {u.referrals_made}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{rows.length === 0 ? "0" : `${skip + 1}–${Math.min(skip + 25, total)}`} of {total}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={skip <= 0} onClick={() => setSkip(Math.max(0, skip - 25))} data-testid="page-prev">Prev</Button>
          <Button variant="outline" size="sm" disabled={skip + 25 >= total} onClick={() => setSkip(skip + 25)} data-testid="page-next">Next</Button>
        </div>
      </div>

      <UserDetailDrawer
        user={drawerUser}
        open={!!drawerUser}
        onClose={() => setDrawerUser(null)}
        onImpersonate={impersonate}
        onResetPw={resetPassword}
        onSuspend={toggleSuspend}
        onDelete={(u) => setConfirmDelete(u)}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes {confirmDelete?.email} and cascades all their properties, deals, and conversations.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(confirmDelete.id)} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
